import { type ThreadListFilter } from '@littlebird/sdk';
import { useNavigate } from '@tanstack/react-router';
import { useState } from 'react';

import { Icons } from '@/assets/icons/icons';
import { XStack } from '@/components/ui';
import { DropdownMenu } from '@/components/ui/dropdown-menu';
import { HeaderSearch } from '@/design-system-v2/header-search';
import { Page } from '@/design-system-v2/page';
import { trackEvent } from '@/utils/analytics';

import { AllChatsList } from './AllChatsList';

const CHAT_FILTER_OPTIONS: Array<{
  value: ThreadListFilter;
  label: string;
}> = [
  { value: 'all', label: 'All chats' },
  { value: 'favorites', label: 'Favorites' },
  { value: 'in-project', label: 'In a project' },
  { value: 'no-project', label: 'Not in a project' },
  { value: 'from-meeting', label: 'From a meeting' },
  { value: 'archived', label: 'Archived' },
];

export function AllChatsOverview({
  filter,
  onFilterChange,
}: {
  filter: ThreadListFilter;
  onFilterChange: (filter: ThreadListFilter) => void;
}) {
  const [query, setQuery] = useState('');

  return (
    <div className="h-full relative flex flex-col">
      <Page.HeaderActions>
        <XStack className="items-center gap-3">
          <HeaderSearch
            value={query}
            onChange={setQuery}
            placeholder="Search chats..."
            aria-label="Search chats"
          />
          <ChatsFilterDropdown
            filter={filter}
            onFilterChange={onFilterChange}
          />
          <NewChatButton />
        </XStack>
      </Page.HeaderActions>
      <AllChatsList key={filter} query={query} filter={filter} />
    </div>
  );
}

function NewChatButton() {
  const navigate = useNavigate();

  return (
    <Page.HeaderButton
      icon={Icons.plus}
      onClick={() => navigate({ to: '/chats', search: { new: true } })}
    >
      New chat
    </Page.HeaderButton>
  );
}

function ChatsFilterDropdown({
  filter,
  onFilterChange,
}: {
  filter: ThreadListFilter;
  onFilterChange: (filter: ThreadListFilter) => void;
}) {
  const filterLabel = CHAT_FILTER_OPTIONS.find(
    (option) => option.value === filter
  )?.label;

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <Page.HeaderButton icon={Icons.listFilter} aria-label="Filter chats">
          {filter === 'all' ? 'Filter' : filterLabel}
        </Page.HeaderButton>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content
        align="end"
        className="min-w-48"
        fit
        onCloseAutoFocus={(event) => event.preventDefault()}
      >
        <DropdownMenu.Label>Show</DropdownMenu.Label>
        {CHAT_FILTER_OPTIONS.map((option) => (
          <DropdownMenu.Item
            key={option.value}
            onClick={() => {
              trackEvent('ALL_CHATS_FILTER_CHANGED', {
                filter: option.value,
              });
              onFilterChange(option.value);
            }}
          >
            <span className="flex-1 truncate">{option.label}</span>
            {filter === option.value ? (
              <Icons.check className="ml-auto size-3.5 shrink-0 opacity-75" />
            ) : null}
          </DropdownMenu.Item>
        ))}
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  );
}
